<template>
    <div class="listingSettings">
        <CompassTooltip :content="title" :arrow="false">
            <CompassIconButton
                icon="settings"
                class="listingSettings__button"
                @click="openDialog"
            />
        </CompassTooltip>

        <CompassModal
            :title="title"
            :primaryAction="primaryAction"
            :secondaryActions="secondaryActions"
            :visible="isOpen"
            :size="CompassEnums.ModalSize.SMALL"
            @update:visible="cancelDialog"
        >
            <ListingSettingsColumn
                :columns="columns"
                :searchable="searchableColumns"
                @updateColumns="
                    (cols: ListingSettingsColumnData[]) => {
                        columns = cols;
                    }
                "
            />
        </CompassModal>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import ListingSettingsColumn from 'vcomponents/common/listing/settings/ListingSettingsColumn.vue';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import { cloneDeep } from 'lodash';
import type { ListingSettingsColumnData } from 'js/types/components/listing/listing.type';
import { CompassEvent } from 'js/types/events';

interface Props {
    searchableColumns?: boolean;
}

withDefaults(defineProps<Props>(), {
    searchableColumns: true,
});

const emit = defineEmits<{
    updateColumns: [columns: ListingSettingsColumnData[]];
    resetColumns: [];
}>();

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();

const isOpen = ref(false);
const columns = ref<ListingSettingsColumnData[]>([]);
const snapshot = ref<ListingSettingsColumnData[]>([]);

const title = $t.__('listing.settings.ListingSettings.title');

const storeColumns = computed(() => store.state.listingSettingsStores?.columns ?? []);

const notifySuccess = (): void => {
    $pushToast({
        type: CompassEnums.ToastType.SUCCESS,
        icon: true,
        text: $t.__('listing.settings.ListingSettings.push_message.success'),
    });
};

const primaryAction = {
    label: $t.__('common.actions.apply'),
    action: () => {
        emit('updateColumns', cloneDeep(columns.value));
        notifySuccess();
        isOpen.value = false;
    },
};

const secondaryActions = [
    {
        label: $t.__('common.actions.reset'),
        action: () => {
            emit('resetColumns');
            notifySuccess();
            isOpen.value = false;
        },
    },
];

const openDialog = (): void => {
    snapshot.value = cloneDeep(columns.value);
    isOpen.value = true;
};

const cancelDialog = (): void => {
    columns.value = snapshot.value;
    isOpen.value = false;
};

const syncFromStore = (): void => {
    columns.value = cloneDeep(storeColumns.value);
};

watch(storeColumns, syncFromStore, { immediate: true });

EventBus.on(CompassEvent.TABLE_COLUMNS_RESIZED, syncFromStore);

onBeforeUnmount(() => {
    EventBus.off(CompassEvent.TABLE_COLUMNS_RESIZED, syncFromStore);
});
</script>
